import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Screen } from '@/components/Screen'
import { Button } from '@/components/Button'
import { Chip } from '@/components/Chip'
import { Field, Select, TextInput } from '@/components/Field'
import { BottomSheet } from '@/components/BottomSheet'
import { SkeletonList } from '@/components/Skeleton'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/stores/appStore'
import { BarcodeScanner } from '@/components/BarcodeScanner'
import type { OwnedItem, OwnedItemCategory, OwnedItemStatus } from '@/lib/types'

const CATEGORIES: OwnedItemCategory[] = [
  'clothing', 'footwear', 'sleep_gear', 'feeding_gear', 'safety_equipment',
  'transport', 'furniture', 'toy', 'book', 'electronic', 'sports_equipment',
  'music_instrument', 'school_supply', 'document', 'other',
]

const STATUSES: OwnedItemStatus[] = ['wish', 'registered', 'ordered', 'in_use', 'stored', 'outgrown', 'sold', 'discarded', 'recalled', 'lost']

type ViewFilter = 'all' | 'in_use' | 'wish' | 'outgrown'

export function OwnedItemsScreen() {
  const familyId = useAppStore((s) => s.familyId)
  const [filter, setFilter] = useState<ViewFilter>('all')
  const [editing, setEditing] = useState<OwnedItem | 'new' | null>(null)

  const q = useQuery({
    enabled: !!familyId,
    queryKey: ['owned-items', familyId],
    queryFn: async () => {
      const { data, error } = await supabase.from('owned_items')
        .select('*').eq('family_id', familyId!)
        .order('updated_at', { ascending: false }).limit(500)
      if (error) throw error
      return (data ?? []) as OwnedItem[]
    },
  })

  const items = useMemo(() => {
    const all = q.data ?? []
    if (filter === 'all') return all
    return all.filter((i) => i.status === filter)
  }, [q.data, filter])

  return (
    <Screen title="Items + wishlist" back>
      <div className="px-4 pt-3 pb-6 space-y-4">
        <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4">
          {(['all', 'in_use', 'wish', 'outgrown'] as ViewFilter[]).map((f) => (
            <Chip key={f} emphasis={filter === f ? 'filled' : 'quiet'} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : f === 'in_use' ? 'In use' : f === 'wish' ? 'Wishlist' : 'Outgrown'}
            </Chip>
          ))}
          <Chip emphasis="quiet" onClick={() => setEditing('new')}>+ Add</Chip>
        </div>

        {q.isLoading && <SkeletonList rows={5} />}
        {q.data && items.length === 0 && (
          <div className="glass-tile p-6 text-center text-body text-text-secondary">
            {filter === 'wish' ? 'No wishlist items yet.' : `No ${filter === 'all' ? '' : filter} items yet.`}
            <div className="mt-2">
              <Button variant="ghost" size="md" onClick={() => setEditing('new')}>+ Add first item</Button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {items.map((it, i) => (
            <motion.button
              key={it.id}
              initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.18, delay: Math.min(i * 0.02, 0.4) }}
              type="button" onClick={() => setEditing(it)}
              className="glass-tile p-3 w-full text-left"
            >
              <div className="flex items-baseline justify-between gap-3">
                <div className="text-body text-text-primary font-medium truncate">{it.display_name}</div>
                <div className="text-tag text-text-tertiary shrink-0">{it.status}</div>
              </div>
              <div className="text-caption text-text-tertiary mt-0.5">
                {it.category}{it.size_label ? ` · ${it.size_label}` : ''}{it.brand ? ` · ${it.brand}` : ''}
              </div>
              {it.status === 'in_use' && it.brand && (
                <a
                  href={`https://www.saferproducts.gov/PublicSearch/Search?SearchTerm=${encodeURIComponent(it.brand + ' ' + it.display_name)}`}
                  target="_blank" rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="mt-1 inline-block text-tag text-accent-coral underline"
                >
                  Check CPSC recalls ↗
                </a>
              )}
            </motion.button>
          ))}
        </div>

        {editing && <ItemSheet familyId={familyId!} initial={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => q.refetch()} />}
      </div>
    </Screen>
  )
}

function ItemSheet({ familyId, initial, onClose, onSaved }: { familyId: string; initial: OwnedItem | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(initial?.display_name ?? '')
  const [category, setCategory] = useState<OwnedItemCategory>(initial?.category ?? 'other')
  const [status, setStatus] = useState<OwnedItemStatus>(initial?.status ?? 'wish')
  const [size, setSize] = useState(initial?.size_label ?? '')
  const [brand, setBrand] = useState(initial?.brand ?? '')
  const [model, setModel] = useState(initial?.model ?? '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [scanning, setScanning] = useState(false)
  const [serial, setSerial] = useState(initial?.serial_number ?? '')

  async function save() {
    setBusy(true); setErr(null)
    try {
      const row = {
        family_id: familyId,
        display_name: name.trim(),
        category, status,
        size_label: size.trim() || null,
        brand: brand.trim() || null,
        model: model.trim() || null,
        serial_number: serial.trim() || null,
      }
      if (initial) {
        const { error } = await supabase.from('owned_items').update(row).eq('id', initial.id)
        if (error) throw new Error(error.message)
      } else {
        const { error } = await supabase.from('owned_items').insert(row)
        if (error) throw new Error(error.message)
      }
      onSaved(); onClose()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally { setBusy(false) }
  }

  async function remove() {
    if (!initial || !window.confirm(`Remove "${initial.display_name}"?`)) return
    setBusy(true)
    try {
      const { error } = await supabase.from('owned_items').delete().eq('id', initial.id)
      if (error) throw new Error(error.message)
      onSaved(); onClose()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally { setBusy(false) }
  }

  return (
    <BottomSheet
      open={true}
      onClose={onClose}
      title={initial ? 'Edit item' : 'Add item'}
      action={<Button variant="primary" size="lg" loading={busy} onClick={save} className="w-full">Save</Button>}
    >
      <div className="space-y-3 pt-3">
        <Field label="Name"><TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Snoo bassinet" autoFocus required /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <Select value={category} onChange={(e) => setCategory(e.target.value as OwnedItemCategory)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
            </Select>
          </Field>
          <Field label="Status">
            <Select value={status} onChange={(e) => setStatus(e.target.value as OwnedItemStatus)}>
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </Select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Size (optional)"><TextInput value={size} onChange={(e) => setSize(e.target.value)} placeholder="0-3M" /></Field>
          <Field label="Brand (optional)"><TextInput value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Happiest Baby" /></Field>
        </div>
        <Field label="Model (optional)"><TextInput value={model} onChange={(e) => setModel(e.target.value)} /></Field>

        <Field label="Serial / Barcode (optional)" hint="Tap Scan to use the camera">
          <div className="flex gap-2">
            <TextInput value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="ABC123 or scan…" />
            <Button variant="ghost" size="md" onClick={() => setScanning(true)}>Scan</Button>
          </div>
        </Field>

        <BarcodeScanner
          open={scanning}
          onClose={() => setScanning(false)}
          onDetected={(code) => { setSerial(code); setScanning(false) }}
        />

        {brand && (
          <a href={`https://www.saferproducts.gov/PublicSearch/Search?SearchTerm=${encodeURIComponent(brand + ' ' + name)}`}
             target="_blank" rel="noopener noreferrer" className="text-caption text-accent-coral underline block">
            Search CPSC recalls for this ↗
          </a>
        )}

        {initial && (
          <button type="button" onClick={remove} className="text-caption underline" style={{ color: '#D14758' }}>
            Remove item
          </button>
        )}
        {err && <div className="rounded-r-sm p-3 text-caption" style={{ background: 'rgba(232,94,110,0.12)', color: '#D14758' }}>{err}</div>}
      </div>
    </BottomSheet>
  )
}
